import React from 'react';
import {Table, TableColumnConfig, Skeleton} from '@gravity-ui/uikit';
import {EmptyState} from '../EmptyState';

const DEFAULT_SKELETON_ROWS = 5;

interface TableProps<T> {
  data: T[];
  columns: TableColumnConfig<T>[];
  isLoading: boolean;
  skeletonData?: any[];
  skeletonColumns?: TableColumnConfig<any>[];
  emptyStateTitle?: string;
  emptyStateDescription?: string;
  edgePadding?: boolean;
  skeletonRowCount?: number;
  onRowClick?: (item: T) => void;
  getRowClassNames?: (item: T, index: number) => string[];
}

export const DataTable = <T extends {}>({
  data,
  columns,
  isLoading,
  skeletonData: customSkeletonData,
  skeletonColumns,
  emptyStateTitle = 'Нет данных для отображения',
  emptyStateDescription = 'Попробуйте изменить фильтры или добавить новые записи.',
  edgePadding = false,
  skeletonRowCount = DEFAULT_SKELETON_ROWS,
  onRowClick,
  getRowClassNames,
}: TableProps<T>) => {
  const defaultSkeletonData = React.useMemo(() => Array(skeletonRowCount).fill({}), [skeletonRowCount]);
  const actualSkeletonData = customSkeletonData ?? defaultSkeletonData;

  // Generate default skeleton columns if not provided, based on the main columns
  const defaultSkeletonColumns = React.useMemo<TableColumnConfig<any>[]>(() => {
    return columns.map((col) => ({
      id: col.id,
      name: col.name,
      width: col.width, // Preserve width for layout consistency
      template: () => <Skeleton style={{width: '100%', height: 16}} />,
    }));
  }, [columns]);

  const actualSkeletonColumns = skeletonColumns ?? defaultSkeletonColumns;

  if (isLoading) {
    return (
      <Table<T> edgePadding={edgePadding} width={'max'} data={actualSkeletonData} columns={actualSkeletonColumns} />
    );
  }

  if (data.length > 0) {
    return <Table<T>
      edgePadding={edgePadding}
      width={'max'}
      data={data}
      columns={columns}
      onRowClick={onRowClick}
      // Одноимённый проп Table помечен @deprecated в пользу getRowDescriptor —
      // наружу сохраняем прежнюю сигнатуру, внутрь передаём актуальный проп.
      getRowDescriptor={getRowClassNames && ((item, index) => ({classNames: getRowClassNames(item, index)}))}
    />;
  }

  return <EmptyState title={emptyStateTitle} description={emptyStateDescription} />;
};
